/**
 * OfflineDraftsPanel.jsx
 * =========================
 * Panneau "Mes brouillons hors-ligne" affiché en haut de la page Découverte.
 *
 * Pour chaque draft :
 *  - prospect + adresse (extraits du payload)
 *  - status (pending/syncing/synced/error)
 *  - action : reprendre / supprimer / re-sync
 *
 * Si aucune draft → ne s'affiche pas.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trash2, RefreshCw, Edit3, AlertCircle, CheckCircle2, Clock3, CloudOff, ChevronDown, ChevronUp, RotateCcw } from 'lucide-react';
import { listDrafts, deleteDraft } from '../../lib/offlineStorage';
import { useOffline } from '../../contexts/OfflineContext';
import { toast } from 'sonner';

/* eslint-disable react/no-unescaped-entities */

const STATUS_CONFIG = {
    pending: { label: 'En attente', icon: Clock3, color: 'text-amber-300', bg: 'bg-amber-500/15 border-amber-500/40' },
    syncing: { label: 'Sync en cours', icon: RefreshCw, color: 'text-blue-300', bg: 'bg-blue-500/15 border-blue-500/40' },
    synced: { label: 'Synchronisée', icon: CheckCircle2, color: 'text-emerald-300', bg: 'bg-emerald-500/15 border-emerald-500/40' },
    error: { label: 'Erreur', icon: AlertCircle, color: 'text-red-300', bg: 'bg-red-500/15 border-red-500/40' },
};

export default function OfflineDraftsPanel({ onResumeDraft }) {
    const { syncNow, syncing, isOnline, pendingCount, retryDraft } = useOffline();
    const [drafts, setDrafts] = useState([]);
    const [expanded, setExpanded] = useState(false);

    const refresh = useCallback(async () => {
        try {
            const all = await listDrafts();
            // On garde les non-synced et les synced du jour
            const today = new Date().toISOString().slice(0, 10);
            const filtered = all.filter(
                (d) => d.sync_status !== 'synced' || (d.synced_at || '').slice(0, 10) === today
            );
            setDrafts(filtered);
        } catch (e) {
            setDrafts([]);
        }
    }, []);

    useEffect(() => {
        refresh();
        const t = setInterval(refresh, 5000);
        return () => clearInterval(t);
    }, [refresh, syncing, pendingCount]);

    if (drafts.length === 0) return null;

    const handleDelete = async (uuid) => {
        if (!window.confirm("Supprimer ce brouillon ? Cette action est irréversible.")) return;
        await deleteDraft(uuid);
        toast.success('Brouillon supprimé');
        refresh();
    };

    const formatCountdown = (iso) => {
        if (!iso) return null;
        const ms = Date.parse(iso) - Date.now();
        if (ms <= 0) return 'Bientôt…';
        const mins = Math.floor(ms / 60000);
        if (mins < 1) return 'Bientôt…';
        if (mins < 60) return `${mins}min`;
        const hrs = Math.floor(mins / 60);
        return `${hrs}h`;
    };

    const pendingDrafts = drafts.filter((d) => d.sync_status !== 'synced');

    return (
        <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border border-[#FF914D]/30 bg-gradient-to-br from-[#FF914D]/5 via-card to-[#681793]/5 overflow-hidden"
            data-testid="offline-drafts-panel"
        >
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left active:scale-[0.99] transition"
                data-testid="offline-drafts-toggle"
            >
                <div className="flex items-center gap-2 min-w-0">
                    <CloudOff className="w-4 h-4 text-[#FF914D] flex-shrink-0" />
                    <div className="min-w-0">
                        <p className="text-sm font-bold text-foreground">
                            Brouillons hors-ligne <span className="text-[#FF914D]">({drafts.length})</span>
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                            {pendingDrafts.length > 0
                                ? `${pendingDrafts.length} à synchroniser`
                                : 'Tous synchronisés'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {isOnline && pendingDrafts.length > 0 && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); syncNow(); }}
                            disabled={syncing}
                            className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-gradient-to-r from-[#681793] to-[#FF914D] text-white shadow-sm hover:brightness-110 disabled:opacity-50"
                            data-testid="drafts-sync-btn"
                        >
                            {syncing ? <RefreshCw className="w-3 h-3 animate-spin" /> : 'Sync'}
                        </button>
                    )}
                    {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                </div>
            </button>

            <AnimatePresence>
                {expanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden border-t border-white/5"
                    >
                        <div className="p-3 space-y-2 max-h-[40vh] overflow-y-auto">
                            {drafts.map((d) => {
                                const conf = STATUS_CONFIG[d.sync_status] || STATUS_CONFIG.pending;
                                const Icon = conf.icon;
                                const prospect = d.payload?.prospect || {};
                                const property = d.payload?.property || {};
                                const name = `${prospect.first_name || ''} ${prospect.last_name || ''}`.trim() || 'Sans nom';
                                const addr = property.address || 'Adresse non renseignée';
                                const retryCount = d.retry_count || 0;
                                const countdown = d.sync_status === 'error' ? formatCountdown(d.next_retry_at) : null;
                                return (
                                    <div
                                        key={d.client_uuid}
                                        className={`rounded-xl p-3 border ${conf.bg} flex items-start gap-2`}
                                        data-testid={`draft-item-${d.client_uuid}`}
                                    >
                                        <div className="flex-shrink-0 mt-0.5">
                                            <Icon className={`w-4 h-4 ${conf.color} ${d.sync_status === 'syncing' ? 'animate-spin' : ''}`} />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-bold text-foreground truncate">{name}</p>
                                            <p className="text-[11px] text-muted-foreground truncate">{addr}</p>
                                            <div className="flex flex-wrap items-center gap-2 mt-1">
                                                <p className={`text-[10px] font-bold ${conf.color}`}>{conf.label}</p>
                                                {retryCount > 0 && d.sync_status !== 'synced' && (
                                                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-white/5 text-muted-foreground">
                                                        Tentative {retryCount}
                                                    </span>
                                                )}
                                                {countdown && (
                                                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-white/5 text-amber-300">
                                                        Retry dans {countdown}
                                                    </span>
                                                )}
                                            </div>
                                            {d.last_sync_error && (
                                                <p className="text-[10px] text-red-300/80 mt-1 italic line-clamp-2">{d.last_sync_error}</p>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-1 flex-shrink-0">
                                            {d.sync_status === 'error' && (
                                                <button
                                                    type="button"
                                                    onClick={() => retryDraft(d.client_uuid)}
                                                    title="Réessayer maintenant"
                                                    disabled={syncing}
                                                    className="p-1.5 rounded-lg text-muted-foreground hover:text-emerald-400 hover:bg-white/5 disabled:opacity-40"
                                                    data-testid={`draft-retry-${d.client_uuid}`}
                                                >
                                                    <RotateCcw className="w-3.5 h-3.5" />
                                                </button>
                                            )}
                                            {onResumeDraft && d.sync_status !== 'synced' && (
                                                <button
                                                    type="button"
                                                    onClick={() => onResumeDraft(d)}
                                                    title="Reprendre ce brouillon"
                                                    className="p-1.5 rounded-lg text-muted-foreground hover:text-[#FF914D] hover:bg-white/5"
                                                    data-testid={`draft-resume-${d.client_uuid}`}
                                                >
                                                    <Edit3 className="w-3.5 h-3.5" />
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => handleDelete(d.client_uuid)}
                                                title="Supprimer"
                                                className="p-1.5 rounded-lg text-muted-foreground hover:text-red-400 hover:bg-white/5"
                                                data-testid={`draft-delete-${d.client_uuid}`}
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
}
